import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { BakingSession, FERMENTATION_PROCESSES } from '../../types/baking';
import { SessionTimelinePanel } from './SessionTimelinePanel';
import './BakingInProgressWidget.css';

interface BakingInProgressWidgetProps {
  session: BakingSession;
}

export function BakingInProgressWidget({ session }: BakingInProgressWidgetProps) {
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [nextStep, setNextStep] = useState<'shaping' | 'baking' | 'completed'>('shaping');
  const [progress, setProgress] = useState<number>(0);
  const [shouldShow, setShouldShow] = useState<boolean>(false);
  const [showTimelinePanel, setShowTimelinePanel] = useState(false);

  useEffect(() => {
    // Calculate times for the session
    const calculateTimes = () => {
      const now = new Date();
      const bakeTime = new Date(session.targetBakeTime);
      
      // Find the fermentation process
      const process = FERMENTATION_PROCESSES.find(p => p.id === session.fermentationType);
      if (!process) return;
      
      // Calculate kneading time
      const kneadingTime = new Date(bakeTime);
      kneadingTime.setHours(kneadingTime.getHours() - process.totalTime);
      
      // Calculate shaping time
      const shapingTime = new Date(bakeTime);
      shapingTime.setHours(shapingTime.getHours() - process.proofTime);
      
      // Determine if we should show the widget
      // Only show after kneading time has passed and before baking is completed
      const kneadingPassed = now > kneadingTime;
      const bakingPassed = now > bakeTime;
      
      if (bakingPassed) {
        // Hide widget after baking time
        setShouldShow(false);
        setNextStep('completed');
        return;
      } else if (kneadingPassed) {
        setShouldShow(true);
        
        // Determine next step and calculate time remaining
        if (now < shapingTime) {
          setNextStep('shaping');
          const timeToShaping = shapingTime.getTime() - now.getTime();
          
          // Calculate progress percentage for the animation
          const totalBulkTime = shapingTime.getTime() - kneadingTime.getTime();
          const elapsedBulkTime = now.getTime() - kneadingTime.getTime();
          const bulkProgress = Math.min(100, (elapsedBulkTime / totalBulkTime) * 100);
          setProgress(bulkProgress);
          
          // Format time remaining
          setTimeRemaining(formatTimeRemaining(timeToShaping));
        } else {
          setNextStep('baking');
          const timeToBaking = bakeTime.getTime() - now.getTime();
          
          // Calculate progress percentage for the animation
          const totalProofTime = bakeTime.getTime() - shapingTime.getTime();
          const elapsedProofTime = now.getTime() - shapingTime.getTime();
          const proofProgress = Math.min(100, (elapsedProofTime / totalProofTime) * 100);
          setProgress(proofProgress);
          
          // Format time remaining
          setTimeRemaining(formatTimeRemaining(timeToBaking));
        }
      } else {
        // Don't show widget before kneading time
        setShouldShow(false);
      }
    };
    
    // Initial calculation
    calculateTimes();
    
    // Update every minute
    const interval = setInterval(calculateTimes, 60000);
    
    return () => clearInterval(interval);
  }, [session]);
  
  // Don't render if we shouldn't show the widget
  if (!shouldShow) return null;
  
  return (
    <>
      <div 
        className="fixed bottom-24 md:bottom-8 right-4 z-50 cursor-pointer"
        onClick={() => setShowTimelinePanel(!showTimelinePanel)}
      >
        <div className="relative w-20 h-20 rounded-full shadow-[0_0_15px_rgba(0,0,0,0.2),0_0_30px_rgba(0,0,0,0.1)]">
        {/* White background circle */}
        <div className="absolute inset-0 bg-[#FCF7F5] rounded-full"></div>
        
        {/* Progress circle with gradient */}
        <div 
          className="absolute inset-1 rounded-full overflow-hidden progress-circle"
          style={{ '--progress': `${progress}%` } as React.CSSProperties}
        >
          <div className="progress-circle-inner"></div>
        </div>
        
        {/* Inner white circle */}
        <div className="absolute inset-2 bg-[#FCF7F5] rounded-full flex flex-col items-center justify-center">
          <span className="text-gray-600 font-brand text-xl leading-none mt-1">{timeRemaining}</span>
          <span className="text-gray-500 text-[10px] mt-0">
            to {nextStep}
          </span>
        </div>
      </div>
      </div>

      <SessionTimelinePanel
        session={session}
        isOpen={showTimelinePanel}
        onClose={() => setShowTimelinePanel(false)}
      />
    </>
  );
}

// Helper function to format time remaining
function formatTimeRemaining(milliseconds: number): string {
  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}